<script setup>
  import { SENTIMENT_BY_CODE, SENTIMENT_COLORS } from "@/lib/chartPalettes";
  import { ref, computed, inject, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Pencil, Download, Quote } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import useMyFetch from "@stores/useMyFetch";
  import { useVerbatimsStore } from "@stores/verbatims";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
  import { POPULATION_AUTO_COLORS, populationColor } from "@/lib/chartPalettes";
  import EditVerbatimThemesDialog from "./EditVerbatimThemesDialog.vue";
  import { ImageLightbox } from "@/components/episto-ui/image-lightbox";

  const PAGE_SIZE = 25;

  const props = defineProps({
    questionId: { type: Number, required: true },
    cardType: { type: String, default: "q_open" },
    recodeCategoryCode: { type: [Number, String], default: null },
    withSentiment: { type: Boolean, default: false },
    fullHeight: { type: Boolean, default: false },
    fill: { type: Boolean, default: false },
    showTranslations: { type: Boolean, default: true },
  });

  const emit = defineEmits(["filters-change"]);

  const MEDIA_TYPE_MAP = { q_image: "image", q_video: "video", q_audio: "audio" };
  const mediaType = computed(() => MEDIA_TYPE_MAP[props.cardType] || "text");

  const { effectiveShowAllPopulation } = storeToRefs(useResultsV2Store());
  const { canAnalyzeSurvey } = storeToRefs(useSurveyStore());
  const populationsStore = useResultsV2PopulationsStore();
  const { populations: viewPopulations } = storeToRefs(populationsStore);
  const verbatimsStore = useVerbatimsStore();

  const cardPopulations = inject("cardPopulations", null);

  const populations = computed(() => {
    if (cardPopulations?.value) {
      return cardPopulations.value.map((pop) => ({ name: pop.label, color: pop.color, filters: pop.filters || {} }));
    }
    const result = [];
    if (effectiveShowAllPopulation.value) {
      result.push({ name: "All", color: POPULATION_AUTO_COLORS[0], filters: {} });
    }
    viewPopulations.value.forEach((pop) => {
      result.push({
        name: pop.search_group?.name,
        color: populationColor(pop.search_group?.token, viewPopulations.value),
        filters: pop.search_group?.filters || {},
      });
    });
    return result;
  });

  const showPopulationTabs = computed(() => populations.value.length > 1);
  const activePopIndex = ref(0);
  const activeFilters = computed(() => populations.value[activePopIndex.value]?.filters || {});

  const verbatims = ref([]);
  const page = ref(0);
  const totalPages = ref(1);
  const totalRespondents = ref(0);
  const loading = ref(false);

  const countLabel = computed(() => {
    const shown = verbatims.value.length;
    const total = totalRespondents.value;
    if (!total) return "";
    return `${shown} / ${total}`;
  });

  async function loadMore() {
    if (loading.value) return;
    loading.value = true;

    const nextPage = page.value + 1;
    let data;

    if (props.recodeCategoryCode != null) {
      // Use recode_verbatims endpoint
      const body = {
        category_code: props.recodeCategoryCode,
        filters: activeFilters.value,
        page: nextPage,
        page_size: PAGE_SIZE,
      };
      const response = await useMyFetch(`${useSurveyStore().questionApiPath(props.questionId)}/recode_verbatims`, {
        headers: { "Content-Type": "application/json" },
      })
        .post(body)
        .json();
      data = response.data;
    } else {
      const params = {
        questionId: props.questionId,
        filters: activeFilters.value,
        target: null,
        show_translations: props.showTranslations,
        page: nextPage,
        page_size: PAGE_SIZE,
      };
      ({ data } = await verbatimsStore.setVerbatimsExplore(params));
    }

    if (data.value) {
      const newItems = Array.isArray(data.value.data) ? data.value.data : [];
      verbatims.value = [...verbatims.value, ...newItems];
      page.value = Number.parseInt(data.value.page, 10);
      totalPages.value = Number.parseInt(data.value.total_pages, 10);
      totalRespondents.value = Number.parseInt(data.value.total_respondents, 10);
    }

    loading.value = false;
    if (props.fill) maybeFillMore();
  }

  const listBoxEl = ref(null);
  let autoLoadedPages = 0;

  function maybeFillMore() {
    nextTick(() => {
      const el = listBoxEl.value;
      if (!el || loading.value) return;
      if (page.value >= totalPages.value) return;
      if (autoLoadedPages >= 40) return;
      if (el.scrollHeight > el.clientHeight + 1) return;
      autoLoadedPages += 1;
      loadMore();
    });
  }

  function resetAndLoad() {
    verbatims.value = [];
    page.value = 0;
    totalPages.value = 1;
    totalRespondents.value = 0;
    autoLoadedPages = 0;
    loadMore();
  }

  // Theme/sentiment edits change recode answers → chartsData is now stale.
  // Bypass the lodash memoize cache (keyed by survey.updated_at, which we don't bump
  // on theme edits) so the next refresh actually hits the backend.
  function onThemesSaved() {
    resetAndLoad();
    useResultsV2Store().refreshChartsData({ bypassCache: true });
  }

  function selectPopulation(index) {
    if (index === activePopIndex.value) return;
    activePopIndex.value = index;
  }

  const editDialogOpen = ref(false);
  const editingVerbatim = ref(null);

  function openEditDialog(verbatim) {
    editingVerbatim.value = verbatim;
    editDialogOpen.value = true;
  }

  function sentimentColor(s) {
    return SENTIMENT_BY_CODE[s]?.color || SENTIMENT_COLORS.neutral;
  }
  function sentimentLabel(s) {
    return SENTIMENT_BY_CODE[s]?.label || "";
  }

  const mediaItems = computed(() =>
    verbatims.value.flatMap((verbatim) =>
      String(verbatim.content || "")
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean)
        .map((url, index) => ({ key: `${verbatim.id}-${index}`, url })),
    ),
  );

  const previewImage = ref(null);

  function downloadMediaItem(item) {
    const link = document.createElement("a");
    link.href = `/api/questions/${props.questionId}/media/file?url=${encodeURIComponent(item.url)}`;
    link.click();
  }

  let resizeObserver = null;

  function syncFillObserver() {
    if (props.fill && listBoxEl.value) {
      if (!resizeObserver) {
        resizeObserver = new ResizeObserver(() => maybeFillMore());
        resizeObserver.observe(listBoxEl.value);
      }
      maybeFillMore();
    } else {
      resizeObserver?.disconnect();
      resizeObserver = null;
    }
  }

  onMounted(() => {
    loadMore();
    syncFillObserver();
  });

  watch(() => props.fill, syncFillObserver);

  onBeforeUnmount(() => resizeObserver?.disconnect());

  watch(() => props.questionId, resetAndLoad);
  watch(() => props.recodeCategoryCode, resetAndLoad);
  watch(() => props.showTranslations, resetAndLoad);

  watch(populations, (pops) => {
    if (activePopIndex.value >= pops.length) activePopIndex.value = 0;
  });

  // Reload when the visible population changes (tab switch, population
  // added/removed, "All" toggled) — the verbatims must match its filters.
  watch(activeFilters, (newFilters, oldFilters) => {
    if (JSON.stringify(newFilters) === JSON.stringify(oldFilters)) return;
    resetAndLoad();
  });

  watch(activeFilters, (f) => emit("filters-change", f), { immediate: true });
</script>

<template>
  <div class="flex flex-col">
    <!-- Population tabs -->
    <div
      v-if="showPopulationTabs"
      class="flex flex-wrap items-center gap-1 mb-3 shrink-0 print:hidden">
      <button
        v-for="(pop, index) in populations"
        :key="pop.name"
        class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors"
        :class="activePopIndex === index ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'"
        @click="selectPopulation(index)">
        <span
          class="inline-block h-2 w-2 shrink-0 rounded-full"
          :style="{ backgroundColor: pop.color }" />
        {{ pop.name }}
      </button>
    </div>
    <p
      v-if="showPopulationTabs"
      class="mb-3 hidden text-xs font-medium text-muted-foreground print:block">
      {{ populations[activePopIndex]?.name }}
    </p>

    <!-- Verbatims list -->
    <div
      ref="listBoxEl"
      class="print:max-h-none print:overflow-visible"
      :class="
        fullHeight
          ? 'overflow-y-auto'
          : fill
            ? 'min-h-0 grow basis-[300px] overflow-y-auto'
            : 'max-h-[300px] overflow-y-auto'
      ">
      <!-- Image masonry -->
      <div
        v-if="mediaType === 'image'"
        class="columns-2 sm:columns-3 gap-2 print:columns-auto">
        <div
          v-for="item in mediaItems"
          :key="item.key"
          class="group relative mb-2 break-inside-avoid print:inline-block print:w-[32%] print:align-top">
          <img
            :src="item.url"
            class="w-full cursor-zoom-in rounded-lg"
            @click="previewImage = item.url" />
          <button
            class="absolute right-2 top-2 cursor-pointer rounded-md bg-black/60 p-1.5 text-white opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100"
            title="Download image"
            @click="downloadMediaItem(item)">
            <Download class="h-4 w-4" />
          </button>
        </div>
      </div>

      <!-- Video masonry -->
      <div
        v-else-if="mediaType === 'video'"
        class="columns-2 sm:columns-3 gap-2 print:hidden">
        <div
          v-for="item in mediaItems"
          :key="item.key"
          class="group relative mb-2 break-inside-avoid">
          <video
            controls
            class="w-full rounded-lg">
            <source :src="item.url" />
          </video>
          <button
            class="absolute right-2 top-2 cursor-pointer rounded-md bg-black/60 p-1.5 text-white opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100"
            title="Download video"
            @click="downloadMediaItem(item)">
            <Download class="h-4 w-4" />
          </button>
        </div>
      </div>

      <!-- Audio stack -->
      <div
        v-else-if="mediaType === 'audio'"
        class="flex flex-col gap-3 print:hidden">
        <div
          v-for="item in mediaItems"
          :key="item.key"
          class="flex items-center gap-2 rounded-lg bg-muted p-2">
          <audio
            controls
            :src="item.url"
            class="w-full" />
          <button
            class="shrink-0 cursor-pointer rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
            title="Download audio"
            @click="downloadMediaItem(item)">
            <Download class="h-4 w-4" />
          </button>
        </div>
      </div>

      <!-- Text (default) -->
      <template v-else>
        <div class="flex flex-col gap-2">
          <div
            v-for="verbatim in verbatims"
            :key="verbatim.id"
            class="group flex items-start gap-2.5 rounded-lg bg-muted/40 px-3.5 py-2.5">
            <Quote class="mt-0.5 h-3.5 w-3.5 shrink-0 -scale-x-100 text-muted-foreground/50" />
            <span
              v-if="verbatim.sentiment"
              class="shrink-0 mt-1.5 h-2 w-2 rounded-full"
              :style="{ backgroundColor: sentimentColor(verbatim.sentiment) }"
              :title="sentimentLabel(verbatim.sentiment)" />
            <div class="flex-1 min-w-0 text-13 leading-relaxed">
              {{ verbatim.content }}
            </div>
            <div class="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                v-if="canAnalyzeSurvey && recodeCategoryCode != null && verbatim.source_answer_id"
                class="p-1 rounded text-muted-foreground hover:text-foreground"
                title="Edit themes"
                @click="openEditDialog(verbatim)">
                <Pencil class="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <p
      v-if="mediaType === 'audio' || mediaType === 'video'"
      class="hidden py-4 text-center text-13 text-muted-foreground print:block">
      {{ mediaItems.length }} {{ mediaType }} answers, not included in the PDF.
    </p>

    <EmptyState
      v-if="!loading && verbatims.length === 0"
      title="No verbatims for this question."
      size="sm" />

    <div
      v-if="verbatims.length > 0 || loading"
      class="relative flex items-center justify-center pt-3">
      <Button
        v-if="page < totalPages"
        variant="ghost"
        size="sm"
        class="print:hidden"
        :disabled="loading"
        @click="loadMore">
        {{ loading ? "Loading..." : "Show more" }}
      </Button>
      <span
        v-if="countLabel"
        class="absolute right-0 text-xs text-muted-foreground"
        >{{ countLabel }}</span
      >
    </div>

    <!-- Edit themes dialog -->
    <EditVerbatimThemesDialog
      v-if="editingVerbatim"
      v-model:open="editDialogOpen"
      :question-id="questionId"
      :source-answer-id="editingVerbatim.source_answer_id"
      :verbatim-text="editingVerbatim.content"
      :with-sentiment="withSentiment"
      @saved="onThemesSaved"
    />

    <ImageLightbox
      :src="previewImage"
      :open="!!previewImage"
      @update:open="(value) => { if (!value) previewImage = null; }" />
  </div>
</template>
